import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Check, Plus, X } from 'lucide-react';
import { Button, Card, useToast } from '@components/ui';
import { useBeatPackStore } from '@stores/useBeatPackStore';
import { showSuspensionToastIfNeeded } from '@utils/suspension';
import { cn } from '@utils/classNames';
import { ROUTES } from '@config/constants';

interface AddToBeatPackModalProps {
  onClose: () => void;
  fileIds: string[];
}

type Mode = 'existing' | 'new';

export const AddToBeatPackModal: React.FC<AddToBeatPackModalProps> = ({ onClose, fileIds }) => {
  const { beatPacks, fetchBeatPacks, createBeatPack, addFilesToBeatPack, isLoading } = useBeatPackStore();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('existing');
  const [selectedBeatPackId, setSelectedBeatPackId] = useState<string>('');
  const [newPackName, setNewPackName] = useState('');
  const [newPackDescription, setNewPackDescription] = useState('');
  const normalizedFileIds = useMemo(() => Array.from(new Set(fileIds.filter(Boolean))), [fileIds]);

  useEffect(() => {
    fetchBeatPacks();
  }, [fetchBeatPacks]);

  useEffect(() => {
    if (!selectedBeatPackId && beatPacks.length > 0) {
      setSelectedBeatPackId(beatPacks[0].id);
    }
  }, [beatPacks, selectedBeatPackId]);

  const handleAddToExisting = async () => {
    if (!selectedBeatPackId) {
      showToast('error', 'Beat pack required', 'Please choose a beat pack first.');
      return;
    }

    const isSuspended = await showSuspensionToastIfNeeded(showToast);
    if (isSuspended) return;

    try {
      const selectedPack = beatPacks.find((pack) => pack.id === selectedBeatPackId);
      const result = await addFilesToBeatPack(selectedBeatPackId, normalizedFileIds);

      if (result.addedCount > 0) {
        showToast(
          'success',
          'Tracks added',
          `${result.addedCount} track${result.addedCount !== 1 ? 's' : ''} added to "${selectedPack?.name ?? 'beat pack'}".`
        );
      }

      if (result.skippedCount > 0) {
        showToast(
          'info',
          'Skipped duplicates',
          `${result.skippedCount} track${result.skippedCount !== 1 ? 's were' : ' was'} already in the beat pack.`
        );
      }

      if (result.addedCount === 0 && result.skippedCount > 0) {
        showToast('info', 'No new tracks', 'All selected tracks were already included.');
      }

      onClose();
    } catch (error: any) {
      showToast('error', 'Add failed', error?.message || 'Could not add tracks to beat pack.');
    }
  };

  const handleCreateNew = async () => {
    if (!newPackName.trim()) {
      showToast('error', 'Name required', 'Please enter a name for your beat pack.');
      return;
    }
    if (normalizedFileIds.length === 0) {
      showToast('error', 'No files selected', 'Please select at least one file.');
      return;
    }

    const isSuspended = await showSuspensionToastIfNeeded(showToast);
    if (isSuspended) return;

    try {
      const createdPack = await createBeatPack(newPackName.trim(), newPackDescription.trim(), normalizedFileIds);
      showToast('success', 'Beat pack created', `"${newPackName.trim()}" was created successfully.`);
      onClose();
      navigate(`${ROUTES.SHARING}?edit=${encodeURIComponent(createdPack.id)}`);
    } catch (error: any) {
      showToast('error', 'Creation failed', error?.message || 'Could not create beat pack.');
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999]" onClick={onClose} />
      <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none">
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full h-full sm:h-auto sm:max-w-2xl pointer-events-auto"
        >
          <Card className="w-full h-full sm:h-auto sm:max-h-[90vh] overflow-hidden rounded-none sm:rounded-2xl">
            <div className="sticky top-0 z-20 bg-dark-secondary/95 backdrop-blur border-b border-white/10 px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between">
              <div className="min-w-0">
                <h2 className="font-dela text-lg sm:text-xl text-white leading-tight">Add to beat pack</h2>
                <p className="text-xs sm:text-sm text-white/60 font-inter mt-1 truncate">
                  {normalizedFileIds.length} selected track{normalizedFileIds.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-all flex-shrink-0 ml-3"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-white/60" />
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(100vh-150px)] sm:max-h-[calc(90vh-145px)]">
              <div className="grid grid-cols-2 gap-2 p-1 bg-white/5 border border-white/10 rounded-xl">
                <button
                  type="button"
                  onClick={() => setMode('existing')}
                  className={cn(
                    'rounded-lg px-3 py-2.5 text-xs sm:text-sm font-inter font-semibold transition-all min-h-[42px]',
                    mode === 'existing' ? 'bg-primary text-black shadow-sm' : 'text-white/80 hover:bg-white/10'
                  )}
                >
                  Existing beat pack
                </button>
                <button
                  type="button"
                  onClick={() => setMode('new')}
                  className={cn(
                    'rounded-lg px-3 py-2.5 text-xs sm:text-sm font-inter font-semibold transition-all min-h-[42px]',
                    mode === 'new' ? 'bg-primary text-black shadow-sm' : 'text-white/80 hover:bg-white/10'
                  )}
                >
                  New beat pack
                </button>
              </div>

              {mode === 'existing' ? (
                <div className="space-y-3">
                  {beatPacks.length === 0 ? (
                    <div className="p-4 rounded-xl border border-white/10 bg-white/5 text-white/70 text-sm font-inter leading-relaxed">
                      No beat packs found. Switch to "New beat pack" to create one.
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-[48vh] sm:max-h-[340px] overflow-y-auto pr-1">
                      {beatPacks.map((pack) => (
                        <button
                          key={pack.id}
                          type="button"
                          onClick={() => setSelectedBeatPackId(pack.id)}
                          className={cn(
                            'w-full text-left px-3.5 py-3 rounded-xl border transition-all min-h-[62px] group',
                            selectedBeatPackId === pack.id
                              ? 'border-primary/80 bg-primary/10'
                              : 'border-white/10 bg-white/5 hover:bg-white/10'
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-inter font-semibold text-white text-sm truncate">{pack.name}</p>
                              <p className="font-inter text-xs text-white/60 mt-1">
                                {pack.files.length} track{pack.files.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <div
                              className={cn(
                                'w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-all mt-0.5',
                                selectedBeatPackId === pack.id
                                  ? 'bg-primary border-primary text-black'
                                  : 'border-white/20 text-transparent group-hover:border-white/40'
                              )}
                            >
                              <Check className="w-3.5 h-3.5" />
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block font-inter font-bold text-xs sm:text-sm text-white mb-1.5">
                      Pack name *
                    </label>
                    <input
                      type="text"
                      value={newPackName}
                      onChange={(e) => setNewPackName(e.target.value)}
                      placeholder="e.g. Fire Trap Beats Vol. 1"
                      className="w-full px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-white/40 font-inter focus:outline-none focus:border-primary transition-all min-h-[44px]"
                    />
                  </div>
                  <div>
                    <label className="block font-inter font-bold text-xs sm:text-sm text-white mb-1.5">
                      Description
                    </label>
                    <textarea
                      value={newPackDescription}
                      onChange={(e) => setNewPackDescription(e.target.value)}
                      rows={4}
                      placeholder="Describe your beat pack..."
                      className="w-full px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder:text-white/40 font-inter focus:outline-none focus:border-primary transition-all resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 z-20 bg-dark-secondary/95 backdrop-blur border-t border-white/10 p-4 sm:p-5 flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3">
              <Button onClick={onClose} variant="secondary" className="w-full sm:flex-1 text-sm min-h-[44px]" disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={mode === 'existing' ? handleAddToExisting : handleCreateNew}
                variant="primary"
                className="w-full sm:flex-1 text-sm min-h-[44px] inline-flex items-center justify-center gap-2"
                disabled={isLoading || normalizedFileIds.length === 0 || (mode === 'existing' ? !selectedBeatPackId : !newPackName.trim())}
              >
                {mode === 'existing' ? (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Add tracks</span>
                  </>
                ) : (
                  'Create and add'
                )}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>,
    document.body
  );
};
